/**
 * NL-010 seasonal-demand receipt contract.
 *
 * This module validates only caller-supplied, serializable receipt metadata.
 * It does not run the seasonal engine, select evidence, read a runtime clock,
 * or perform I/O. Demand references are hashes so callers never place raw
 * baseline or adjusted business volumes in a receipt, and a blocked
 * calculation can never be laundered into a receipt after the fact.
 */
import {
  DECIMAL_ROUNDING_CONTRACT_VERSION,
  MAX_DECIMAL_OUTPUT_SCALE,
} from "../../calculator-contracts/decimal";
import {
  SEASONAL_DEMAND_DISABLED_MULTIPLIER,
  SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION,
  SEASONAL_DEMAND_SCENARIOS,
  SEASONAL_DEMAND_UNITS,
  type SeasonalDemandScenario,
  type SeasonalDemandUnit,
} from "./engine";
import {
  SEASONAL_EVIDENCE_MAX_MONTH,
  SEASONAL_EVIDENCE_MIN_MONTH,
  SEASONAL_EVIDENCE_QUALITY_LEVELS,
  SEASONAL_EVIDENCE_USAGE_RIGHTS,
  type SeasonalEvidenceQuality,
  type SeasonalEvidenceUsageRights,
} from "./schema";

export const SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION = "nl010-seasonal-demand-receipt-v1";

/** Blocked calculations intentionally have no receipt representation. */
export const SEASONAL_DEMAND_RECEIPT_OUTCOMES = ["disabled", "ready"] as const;

export type SeasonalDemandReceiptOutcome = (typeof SEASONAL_DEMAND_RECEIPT_OUTCOMES)[number];

/**
 * Privacy-safe demand reference. The canonical baseline and adjusted volumes
 * stay with the caller; the receipt retains only their hashes plus the unit
 * and decimal context needed to interpret those hashes.
 */
export type SeasonalDemandReference = {
  readonly unit: SeasonalDemandUnit;
  readonly outputScale: number;
  readonly baselineDemandHash: string;
  readonly adjustedDemandHash: string;
};

/** Raw adjustment deltas are deliberately unrepresentable in a receipt. */
export type SeasonalManualAdjustmentReference = {
  readonly status: "not-supplied" | "applied";
  readonly label: "user-assumption" | null;
};

/**
 * Immutable provenance references for the evidence record behind a ready
 * receipt. `contentHash` pins the indices actually used, so the receipt does
 * not need to repeat them.
 */
export type SeasonalDemandSourceReference = {
  readonly sourceId: string;
  readonly publisher: string;
  readonly sourceHash: string;
  readonly contentHash: string;
  readonly usageRights: SeasonalEvidenceUsageRights;
  readonly evidenceDate: string;
  readonly refreshDeadline: string;
  readonly evidenceQuality: SeasonalEvidenceQuality;
};

/**
 * The serializable receipt shape a caller-owned calculation flow may
 * construct from a non-blocked engine result. It contains no raw demand
 * volumes, adjustment deltas, or user-entered labels beyond the fixed
 * `user-assumption` marker.
 */
export type SeasonalDemandReceipt = {
  readonly engineContractVersion: typeof SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION;
  readonly decimalRoundingContractVersion: typeof DECIMAL_ROUNDING_CONTRACT_VERSION;
  readonly calculationOutcome: SeasonalDemandReceiptOutcome;
  /** Canonical UTC timestamp (`YYYY-MM-DDTHH:mm:ssZ`), supplied by the caller. */
  readonly calculationTimestamp: string;
  readonly geography: string;
  readonly serviceCategory: string;
  /** The calendar month (1–12) planned; identifies which monthly factor applies. */
  readonly plannedMonth: number;
  /** Null exactly when the calculation was disabled. */
  readonly scenario: SeasonalDemandScenario | null;
  /** Must be the literal disabled multiplier when the calculation was disabled. */
  readonly multiplier: string;
  readonly demand: SeasonalDemandReference;
  readonly manualAdjustment: SeasonalManualAdjustmentReference;
  /** Required for ready receipts; forbidden for disabled receipts. */
  readonly source?: SeasonalDemandSourceReference;
};

export type SeasonalDemandReceiptValidationInput = {
  /** `unknown` keeps this safe at future serialized-data boundaries. */
  readonly receipt?: unknown;
  /** A caller-supplied UTC calendar date; this module never reads the clock. */
  readonly asOfDate: string;
};

export type SeasonalDemandReceiptBlocker =
  | "missing-seasonal-demand-receipt"
  | "invalid-as-of-date"
  | "unrecognized-seasonal-receipt-field"
  | "unsupported-engine-contract-version"
  | "unsupported-decimal-contract-version"
  | "blocked-calculation-cannot-produce-receipt"
  | "invalid-calculation-outcome"
  | "invalid-calculation-timestamp"
  | "calculation-after-as-of-date"
  | "missing-receipt-geography"
  | "missing-receipt-service-category"
  | "invalid-receipt-planned-month"
  | "invalid-seasonal-scenario"
  | "disabled-receipt-must-not-declare-scenario"
  | "invalid-seasonal-multiplier"
  | "disabled-multiplier-must-be-identity"
  | "missing-demand-reference"
  | "unrecognized-seasonal-demand-field"
  | "unsupported-demand-unit"
  | "invalid-output-scale"
  | "missing-baseline-demand-hash"
  | "missing-adjusted-demand-hash"
  | "raw-demand-value-in-receipt"
  | "invalid-manual-adjustment-reference"
  | "missing-seasonal-source"
  | "disabled-receipt-must-not-claim-source"
  | "unrecognized-seasonal-source-field"
  | "missing-source-id"
  | "missing-publisher"
  | "missing-source-hash"
  | "missing-content-hash"
  | "unapproved-source-rights"
  | "invalid-evidence-date"
  | "invalid-source-refresh-deadline"
  | "expired-seasonal-source"
  | "unsupported-evidence-quality";

export type SeasonalDemandReceiptValidationResult = {
  readonly contractVersion: typeof SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION;
  readonly readiness: "ready" | "blocked";
  readonly blockers: readonly SeasonalDemandReceiptBlocker[];
  readonly calculationOutcome: SeasonalDemandReceiptOutcome | null;
};

type UnknownRecord = Readonly<Record<string, unknown>>;

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_UTC_TIMESTAMP = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})Z$/;
const NON_NEGATIVE_DECIMAL = /^\d+(?:\.\d+)?$/;
/** A hash slot holding a plain numeric string is a raw business volume leak. */
const RAW_NUMERIC_VALUE = /^[+-]?\d+(?:[.,]\d+)?$/;

const RECEIPT_FIELDS = [
  "engineContractVersion",
  "decimalRoundingContractVersion",
  "calculationOutcome",
  "calculationTimestamp",
  "geography",
  "serviceCategory",
  "plannedMonth",
  "scenario",
  "multiplier",
  "demand",
  "manualAdjustment",
  "source",
] as const;

const DEMAND_FIELDS = ["unit", "outputScale", "baselineDemandHash", "adjustedDemandHash"] as const;

const MANUAL_ADJUSTMENT_FIELDS = ["status", "label"] as const;

const SOURCE_FIELDS = [
  "sourceId",
  "publisher",
  "sourceHash",
  "contentHash",
  "usageRights",
  "evidenceDate",
  "refreshDeadline",
  "evidenceQuality",
] as const;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function rejectUnrecognizedFields(
  record: UnknownRecord,
  allowedFields: readonly string[],
  blocker: SeasonalDemandReceiptBlocker,
  blockers: SeasonalDemandReceiptBlocker[],
): void {
  if (Object.keys(record).some((field) => !allowedFields.includes(field))) {
    blockers.push(blocker);
  }
}

function nonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isCalendarDate(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_CALENDAR_DATE.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function isCanonicalUtcTimestamp(value: string | null): value is string {
  if (!value) return false;

  const match = ISO_UTC_TIMESTAMP.exec(value);
  if (!match || !isCalendarDate(match[1])) return false;

  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4]);

  return hour <= 23 && minute <= 59 && second <= 59;
}

function isReceiptOutcome(value: unknown): value is SeasonalDemandReceiptOutcome {
  return (
    typeof value === "string" &&
    (SEASONAL_DEMAND_RECEIPT_OUTCOMES as readonly string[]).includes(value)
  );
}

function isScenario(value: unknown): value is SeasonalDemandScenario {
  return (
    typeof value === "string" && (SEASONAL_DEMAND_SCENARIOS as readonly string[]).includes(value)
  );
}

function isPlannedMonth(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= SEASONAL_EVIDENCE_MIN_MONTH &&
    value <= SEASONAL_EVIDENCE_MAX_MONTH
  );
}

function isDemandUnit(value: unknown): value is SeasonalDemandUnit {
  return typeof value === "string" && (SEASONAL_DEMAND_UNITS as readonly string[]).includes(value);
}

function isOutputScale(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_DECIMAL_OUTPUT_SCALE
  );
}

function isKnownUsageRights(value: unknown): value is SeasonalEvidenceUsageRights {
  return (
    typeof value === "string" &&
    (SEASONAL_EVIDENCE_USAGE_RIGHTS as readonly string[]).includes(value)
  );
}

function isEvidenceQuality(value: unknown): value is SeasonalEvidenceQuality {
  return (
    typeof value === "string" &&
    (SEASONAL_EVIDENCE_QUALITY_LEVELS as readonly string[]).includes(value)
  );
}

function validateDemandHash(
  value: unknown,
  missingBlocker: SeasonalDemandReceiptBlocker,
  blockers: SeasonalDemandReceiptBlocker[],
): void {
  const hash = nonEmptyString(value);

  if (!hash) {
    blockers.push(missingBlocker);
    return;
  }

  if (RAW_NUMERIC_VALUE.test(hash)) blockers.push("raw-demand-value-in-receipt");
}

function validateDemandReference(
  value: unknown,
  blockers: SeasonalDemandReceiptBlocker[],
): void {
  const demand = asRecord(value);
  if (!demand) {
    blockers.push("missing-demand-reference");
    return;
  }

  rejectUnrecognizedFields(demand, DEMAND_FIELDS, "unrecognized-seasonal-demand-field", blockers);

  if (!isDemandUnit(demand.unit)) blockers.push("unsupported-demand-unit");
  if (!isOutputScale(demand.outputScale)) blockers.push("invalid-output-scale");
  validateDemandHash(demand.baselineDemandHash, "missing-baseline-demand-hash", blockers);
  validateDemandHash(demand.adjustedDemandHash, "missing-adjusted-demand-hash", blockers);
}

function validateManualAdjustment(
  value: unknown,
  blockers: SeasonalDemandReceiptBlocker[],
): void {
  const manualAdjustment = asRecord(value);
  if (!manualAdjustment) {
    blockers.push("invalid-manual-adjustment-reference");
    return;
  }

  rejectUnrecognizedFields(
    manualAdjustment,
    MANUAL_ADJUSTMENT_FIELDS,
    "invalid-manual-adjustment-reference",
    blockers,
  );

  const status = manualAdjustment.status;
  const label = manualAdjustment.label;

  const validNotSupplied = status === "not-supplied" && label === null;
  const validApplied = status === "applied" && label === "user-assumption";

  if (!validNotSupplied && !validApplied) blockers.push("invalid-manual-adjustment-reference");
}

function validateSourceReference(
  value: unknown,
  asOfDate: string | null,
  blockers: SeasonalDemandReceiptBlocker[],
): void {
  const source = asRecord(value);
  if (!source) {
    blockers.push("missing-seasonal-source");
    return;
  }

  rejectUnrecognizedFields(source, SOURCE_FIELDS, "unrecognized-seasonal-source-field", blockers);

  const evidenceDate = nonEmptyString(source.evidenceDate);
  const refreshDeadline = nonEmptyString(source.refreshDeadline);

  if (!nonEmptyString(source.sourceId)) blockers.push("missing-source-id");
  if (!nonEmptyString(source.publisher)) blockers.push("missing-publisher");
  if (!nonEmptyString(source.sourceHash)) blockers.push("missing-source-hash");
  if (!nonEmptyString(source.contentHash)) blockers.push("missing-content-hash");
  if (!isKnownUsageRights(source.usageRights) || source.usageRights !== "approved") {
    blockers.push("unapproved-source-rights");
  }
  if (!isCalendarDate(evidenceDate)) blockers.push("invalid-evidence-date");
  if (
    !isCalendarDate(refreshDeadline) ||
    (isCalendarDate(evidenceDate) && evidenceDate > refreshDeadline)
  ) {
    blockers.push("invalid-source-refresh-deadline");
  }
  if (!isEvidenceQuality(source.evidenceQuality)) blockers.push("unsupported-evidence-quality");
  if (isCalendarDate(asOfDate) && isCalendarDate(refreshDeadline) && asOfDate > refreshDeadline) {
    blockers.push("expired-seasonal-source");
  }
}

/**
 * Validates a seasonal-demand receipt deterministically. Blocked calculations
 * never validate: only `disabled` and `ready` outcomes are representable, a
 * disabled receipt must carry the identity multiplier with no scenario and no
 * evidence claim, and a ready receipt requires approved, unexpired source
 * provenance. Hash slots holding plain numbers fail closed as raw-value leaks.
 */
export function validateSeasonalDemandReceipt(
  input: SeasonalDemandReceiptValidationInput,
): SeasonalDemandReceiptValidationResult {
  const blockers: SeasonalDemandReceiptBlocker[] = [];
  const asOfDate = nonEmptyString(input.asOfDate);

  if (!isCalendarDate(asOfDate)) blockers.push("invalid-as-of-date");

  const receipt = asRecord(input.receipt);
  if (!receipt) {
    blockers.push("missing-seasonal-demand-receipt");

    return {
      contractVersion: SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION,
      readiness: "blocked",
      blockers,
      calculationOutcome: null,
    };
  }

  rejectUnrecognizedFields(receipt, RECEIPT_FIELDS, "unrecognized-seasonal-receipt-field", blockers);

  if (receipt.engineContractVersion !== SEASONAL_DEMAND_ENGINE_CONTRACT_VERSION) {
    blockers.push("unsupported-engine-contract-version");
  }
  if (receipt.decimalRoundingContractVersion !== DECIMAL_ROUNDING_CONTRACT_VERSION) {
    blockers.push("unsupported-decimal-contract-version");
  }

  const outcome = isReceiptOutcome(receipt.calculationOutcome) ? receipt.calculationOutcome : null;
  if (!outcome) {
    blockers.push(
      receipt.calculationOutcome === "blocked"
        ? "blocked-calculation-cannot-produce-receipt"
        : "invalid-calculation-outcome",
    );
  }

  const calculationTimestamp = nonEmptyString(receipt.calculationTimestamp);
  if (!isCanonicalUtcTimestamp(calculationTimestamp)) {
    blockers.push("invalid-calculation-timestamp");
  } else if (isCalendarDate(asOfDate) && calculationTimestamp.slice(0, 10) > asOfDate) {
    blockers.push("calculation-after-as-of-date");
  }

  if (!nonEmptyString(receipt.geography)) blockers.push("missing-receipt-geography");
  if (!nonEmptyString(receipt.serviceCategory)) blockers.push("missing-receipt-service-category");
  if (!isPlannedMonth(receipt.plannedMonth)) blockers.push("invalid-receipt-planned-month");

  const multiplier = nonEmptyString(receipt.multiplier);
  if (!multiplier || !NON_NEGATIVE_DECIMAL.test(multiplier)) {
    blockers.push("invalid-seasonal-multiplier");
  }

  if (outcome === "disabled") {
    if (receipt.scenario !== null) blockers.push("disabled-receipt-must-not-declare-scenario");
    if (multiplier !== null && multiplier !== SEASONAL_DEMAND_DISABLED_MULTIPLIER) {
      blockers.push("disabled-multiplier-must-be-identity");
    }
    if (receipt.source !== undefined) blockers.push("disabled-receipt-must-not-claim-source");
  } else if (outcome === "ready") {
    if (!isScenario(receipt.scenario)) blockers.push("invalid-seasonal-scenario");
    validateSourceReference(receipt.source, asOfDate, blockers);
  }

  validateDemandReference(receipt.demand, blockers);
  validateManualAdjustment(receipt.manualAdjustment, blockers);

  return {
    contractVersion: SEASONAL_DEMAND_RECEIPT_CONTRACT_VERSION,
    readiness: blockers.length === 0 ? "ready" : "blocked",
    blockers,
    calculationOutcome: blockers.length === 0 ? outcome : null,
  };
}
